import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';

import { cn } from '../../lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

/**
 * Coarse-pointer tap target (44px), reserved as real layout — never bled.
 *
 * The odd one out of the form-control family. `SelectTrigger`, `Input` and
 * `Toggle` are boxes with room to grow, so they take a plain `touch:min-h-[44px]`.
 * A radio is a 16px CIRCLE and has to stay one: `min-h-[44px]` on an
 * `aspect-square rounded-full` element draws a 44px bullet, which is not a bigger
 * tap target, it is a different control. So this is the `Checkbox` pattern, and
 * every number matches `Checkbox` because the geometry does — same 16x16 box,
 * same 1px border:
 *
 *  - `touch:m-3.5` reserves 14px on every side, growing the item's own layout box
 *    to 44x44, so the radio takes the space instead of stealing a neighbour's.
 *  - `touch:after:-inset-[15px]` grows the hit area to fill exactly that
 *    reservation and no further. The 15 ≠ 14 is not a typo: `::after` is offset
 *    from the PADDING box, so the 1px border sits inside the inset and does not
 *    count toward the reach. Matching them naively (`-inset-3.5`) yields 42px.
 *
 * `relative` is load-bearing — without it the overlay positions against whatever
 * ancestor is positioned, which is a hit area over an arbitrary part of the form.
 *
 * The visible circle, and therefore every row of every radio list on a fine
 * pointer, is unchanged. `__tests__/radio-group.test.tsx` derives the target size
 * from all three numbers and goes red if any of them drifts; corollary for callers,
 * as for `Checkbox`: never `scale-*` one, because a transform shrinks the
 * reservation along with the circle.
 */
const RADIO_ITEM_TOUCH_TARGET =
  "touch:m-3.5 touch:after:absolute touch:after:-inset-[15px] touch:after:content-['']";

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'relative aspect-square h-4 w-4 rounded-full border border-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        RADIO_ITEM_TOUCH_TARGET,
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        {/* `fill-primary`, NOT `fill-current` inherited from a `text-primary` on the
            item: the dot is this control's FILL, and `text-primary` is accent text
            (`--primary-strong`, brand blue) since WI a53c1f09. Inheriting it would
            paint a blue dot inside an ink ring, and disagree with `Checkbox` and
            `RadioCard`, which both fill with `bg-primary`. `fill-*` reads
            `colors`, which the textColor split does not touch. */}
        <Circle className="h-2.5 w-2.5 fill-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
